// Shared invoice/settlement math, numbering, and aging/due-date logic,
// extracted so every call site can never independently drift on the
// dollar amount, numbering scheme, or bucket thresholds again (that drift
// is what caused the auto-created-vs-manually-created invoice
// inconsistency in the first place -- see PHASE0_DECISIONS.md's P2-6
// section -- and separately, three disagreeing "gross" formulas existed
// across EditLoadModal/LoadDetailModal/this file before computeLoadGross).

// The true broker-billable total for a load: line haul + fuel surcharge +
// every billable accessorial (accessorial_pay, detention, stop-off, tarp,
// additional, layover, and any custom extra fees). Deliberately excludes
// lumper_fee, late_fee, and invoice_advance -- those are driver-payout
// deductions, never part of what's billed to the broker.
// parseFloat-coerces so this is safe whether called with an already-numeric
// DB row or raw string form state (e.g. from an <input> before it's saved).
const num = (v) => parseFloat(v) || 0;

export function computeLoadGross(load) {
  const extraFeesSum = (() => {
    try {
      return (JSON.parse(load?.extra_fees || '[]') || [])
        .reduce((s, f) => s + num(f.amount), 0);
    } catch {
      return 0;
    }
  })();
  return num(load?.line_haul_rate || load?.rate)
    + num(load?.fuel_surcharge)
    + num(load?.accessorial_pay)
    + num(load?.detention_fee)
    + num(load?.stop_off_fee)
    + num(load?.tarp_fee)
    + num(load?.additional_fee)
    + num(load?.layover_fee)
    + extraFeesSum;
}


// Itemized billable charges for an invoice PDF, pulled from the load:
// line haul first, then fuel surcharge and each accessorial that's
// actually non-zero. Any gap between these and the invoice's own total
// (e.g. the amount was edited after creation) is shown as an explicit
// "Other Charges"/"Adjustment" line so the PDF always adds up to the
// invoice total. With no load (manual invoice) it's one lump line.
export function invoiceChargeLines(load, invoiceTotal, savedItems) {
  // Line items saved from the invoice editor are the source of truth.
  if (Array.isArray(savedItems) && savedItems.length > 0) {
    return savedItems
      .map(i => ({ label: (i.label || '').trim() || 'Charge', amount: num(i.amount) }))
      .filter(i => i.amount !== 0);
  }
  const lines = [];
  const add = (label, amount) => {
    const n = num(amount);
    if (n) lines.push({ label, amount: n });
  };
  if (load) {
    add('Line Haul', load.line_haul_rate || load.rate);
    add('Fuel Surcharge', load.fuel_surcharge);
    add('Detention', load.detention_fee);
    add('Layover', load.layover_fee);
    add('Stop-Off', load.stop_off_fee);
    add('Tarp Fee', load.tarp_fee);
    add('Accessorial', load.accessorial_pay);
    add((load.additional_fee_label || '').trim() || 'Additional Fee', load.additional_fee);
    let extras = load.extra_fees;
    if (typeof extras === 'string') {
      try { extras = JSON.parse(extras || '[]'); } catch { extras = []; }
    }
    for (const f of Array.isArray(extras) ? extras : []) {
      add((f?.label || '').trim() || 'Additional Charge', f?.amount);
    }
  }
  const total = num(invoiceTotal);
  if (lines.length === 0) return total ? [{ label: 'Freight Services', amount: total }] : [];
  const diff = Math.round((total - lines.reduce((s, l) => s + l.amount, 0)) * 100) / 100;
  if (Math.abs(diff) >= 0.01) lines.push({ label: diff > 0 ? 'Other Charges' : 'Adjustment', amount: diff });
  return lines;
}

// Coerces a raw value (a CompanySettings string, a stored Settlement
// field, or live form input) to a number, falling back to `fallback` only
// when the value is genuinely missing/unparseable -- NOT when it parses
// to a legitimate 0. The `parseFloat(x) || fallback` pattern this replaces
// was used all over Pay & Deductions/settlement math and silently
// couldn't distinguish "not configured yet" from "deliberately set to
// 0%" -- a carrier who intentionally zeroed out Carrier Take or Dispatch
// Fee would see 15%/3% charged anyway.
export function numOrDefault(value, fallback) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

// Next sequential INV-#### number given the existing invoices list.
// Malformed/missing invoice_number values are ignored rather than
// crashing or skewing the max.
export function nextInvoiceNumber(existingInvoices) {
  const nums = (existingInvoices || [])
    .map(i => parseInt((i.invoice_number || '').replace('INV-', ''), 10))
    .filter(n => !isNaN(n));
  const next = nums.length ? Math.max(...nums) + 1 : 1001;
  return `INV-${next}`;
}

// Days an invoice with an outstanding balance (Unpaid or partially paid)
// has been sitting since it was invoiced. Returns null for anything that
// isn't a dated, still-owed invoice (Paid/Void invoices have no "aging"
// concept).
export function invoiceAgingDays(inv) {
  if (!['Unpaid', 'Partial'].includes(inv?.status) || !inv?.invoiced_date) return null;
  return Math.floor((Date.now() - new Date(inv.invoiced_date).getTime()) / (1000 * 60 * 60 * 24));
}

// 0-30 green, 31-60 yellow, 61+ red (90+ is still red -- callers add their
// own "90+" label/flag on top rather than a 4th color).
export function agingBucket(days) {
  if (days == null) return null;
  if (days <= 30) return 'green';
  if (days <= 60) return 'yellow';
  return 'red';
}

const NY_TZ = 'America/New_York';

// Company drivers' payroll can't be submitted until Wednesday of any
// given week -- gives accounting Mon/Tue to make sure every load for
// the week has actually been invoiced before running payroll. Owner
// Operators are exempt (see computePayDueAt -- they're paid same/next
// day per load, not on this weekly cycle).
export function isCompanyPayrollWindowOpen(now = new Date()) {
  const dayName = now.toLocaleString('en-US', { timeZone: NY_TZ, weekday: 'short' });
  const order = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return order.indexOf(dayName) >= 3; // Wed(3) through Sat(6)
}

// Resolves a wall-clock date/time in a given IANA timezone to the correct
// UTC instant, DST-aware (no hardcoded -05:00/-04:00 offset -- that would
// be wrong for roughly half the year). Standard "guess in UTC, then
// correct using the timezone's own reported offset" trick.
function zonedTimeToUtc(y, m, d, hh, mm, ss, timeZone) {
  const guessUTC = Date.UTC(y, m - 1, d, hh, mm, ss);
  const asIfLocal = new Date(new Date(guessUTC).toLocaleString('en-US', { timeZone }));
  const offset = guessUTC - asIfLocal.getTime();
  return new Date(guessUTC + offset);
}

// green (not due yet) / yellow (due within 24h or already today) / red
// (overdue) -- same traffic-light language as the invoice aging buckets,
// for a settlement's pay_due_at.
export function payDueBucket(payDueAt, now = new Date()) {
  if (!payDueAt) return null;
  const diffMs = new Date(payDueAt).getTime() - now.getTime();
  if (diffMs < 0) return 'red';
  if (diffMs <= 24 * 60 * 60 * 1000) return 'yellow';
  return 'green';
}

// Calendar-date {y, m, d} for a value that's either a plain "YYYY-MM-DD"
// date (already a calendar date with no time/timezone meaning -- e.g.
// Postgres `date` columns like approved_date -- used exactly as written)
// or a real instant (Date object / full ISO datetime), which gets
// projected into the business's local time zone first. Treating an
// instant's *UTC* calendar day as "the day it happened" is wrong near
// midnight -- 9pm Sunday Eastern is already Monday in UTC.
function calendarDateParts(dateLike) {
  if (typeof dateLike === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateLike)) {
    const [y, m, d] = dateLike.split('-').map(Number);
    return { y, m, d };
  }
  const [m, d, y] = new Date(dateLike)
    .toLocaleString('en-US', { timeZone: NY_TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
    .split('/')
    .map(Number);
  return { y, m, d };
}

function mondayOf({ y, m, d }) {
  const standIn = new Date(Date.UTC(y, m - 1, d));
  const daysSinceMonday = (standIn.getUTCDay() + 6) % 7;
  standIn.setUTCDate(standIn.getUTCDate() - daysSinceMonday);
  return { y: standIn.getUTCFullYear(), m: standIn.getUTCMonth() + 1, d: standIn.getUTCDate() };
}

// True if dateStr falls in the same Monday-Sunday calendar week (business
// local time) as referenceDate. Used to scope the $200/week Personal
// Advance cap to the same week definition the server-side trigger uses
// (Postgres's date_trunc('week', ...) on the date-only approved_date
// column, which has no timezone ambiguity of its own).
export function isSameCalendarWeek(dateStr, referenceDate = new Date()) {
  if (!dateStr) return false;
  const a = mondayOf(calendarDateParts(dateStr));
  const b = mondayOf(calendarDateParts(referenceDate));
  return a.y === b.y && a.m === b.m && a.d === b.d;
}

// When a settlement is due to actually be paid out (a due-date/reminder
// concept only -- this app has no payment-processor integration, so
// nothing here moves money; a human still records the payment after
// handling the real transfer through their own bank).
//
// Owner Operators: paid same day (end of day, America/New_York) if
// delivered before 11am Eastern, otherwise 24 hours after delivery.
// Company Drivers: paid the Friday following the Monday-Sunday week
// their load was delivered in.
export function computePayDueAt(isOwnerOperator, deliveredAt = new Date()) {
  if (isOwnerOperator) {
    const estHour = Number(
      deliveredAt.toLocaleString('en-US', { timeZone: NY_TZ, hour: 'numeric', hour12: false })
    );
    const [m, d, y] = deliveredAt
      .toLocaleString('en-US', { timeZone: NY_TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
      .split('/')
      .map(Number);
    if (estHour < 11) {
      return zonedTimeToUtc(y, m, d, 23, 59, 59, NY_TZ).toISOString();
    }
    return new Date(deliveredAt.getTime() + 24 * 60 * 60 * 1000).toISOString();
  }
  // Company driver: due the Friday after the Monday 12:00am-to-Monday
  // 12:00am week containing delivery, evaluated in America/New_York --
  // the same business-local day a raw UTC day-of-week could get wrong
  // near midnight (9pm Sunday Eastern is already Monday in UTC). Anchor
  // to that week's Monday first (rather than jumping straight to "the
  // next Friday"), which is the only way to get every day of the week
  // right -- a naive "days until Friday, then +7" undercounts for a
  // Fri/Sat/Sun delivery, since "this Friday" for those days already
  // falls in the following week.
  const monday = mondayOf(calendarDateParts(deliveredAt));
  const friday = new Date(Date.UTC(monday.y, monday.m - 1, monday.d + 11)); // Monday + 11 days = the Friday after that week closes
  return zonedTimeToUtc(
    friday.getUTCFullYear(), friday.getUTCMonth() + 1, friday.getUTCDate(),
    23, 59, 59, NY_TZ
  ).toISOString();
}
